import type { PublicTrackingStatus } from "./types";

const API_BASE = (process.env.NEXT_PUBLIC_MAILTECH_API ?? "").replace(
  /\/+$/,
  "",
);

export class PublicTrackingError extends Error {
  readonly status: number;
  readonly code: number | null;
  readonly errorRef?: string;
  constructor(opts: {
    status: number;
    code: number | null;
    message: string;
    errorRef?: string;
  }) {
    super(opts.message);
    this.name = "PublicTrackingError";
    this.status = opts.status;
    this.code = opts.code;
    this.errorRef = opts.errorRef;
  }
}

/**
 * Look up public mail tracking for a 31-digit IMb. Calls the anonymous
 * `/public/v1/tracking/:imb` endpoint DIRECTLY from the browser — no auth,
 * no custom request headers (a simple CORS GET, no preflight) — so the
 * endpoint's per-IP rate limit applies per visitor rather than pooling
 * onto our server. Throws {@link PublicTrackingError} on any non-2xx.
 */
export async function fetchPublicTracking(
  imb: string,
): Promise<PublicTrackingStatus> {
  const res = await fetch(
    `${API_BASE}/public/v1/tracking/${encodeURIComponent(imb)}`,
  );
  if (res.ok) {
    return (await res.json()) as PublicTrackingStatus;
  }

  let code: number | null = null;
  let message = "Tracking lookup failed.";
  let errorRef: string | undefined;
  try {
    const body = (await res.json()) as Record<string, unknown>;
    if (typeof body.code === "number") code = body.code;
    if (typeof body.message === "string") message = body.message;
    if (typeof body["error-ref"] === "string") {
      errorRef = body["error-ref"] as string;
    }
  } catch {
    // Non-JSON error body — keep the defaults.
  }
  throw new PublicTrackingError({
    status: res.status,
    code,
    message,
    errorRef,
  });
}

/**
 * Recipient-friendly message for a tracking lookup failure. Branches on the
 * documented codes (3100 not-found / 3102 rate-limit / 3101 upstream / 2000
 * validation), falling back to HTTP status.
 */
export function mapPublicTrackingError(err: unknown): string {
  if (err instanceof PublicTrackingError) {
    if (err.status === 404 || err.code === 3100) {
      return "No tracking information is available for this barcode.";
    }
    if (err.status === 429 || err.code === 3102) {
      return "Too many requests — please try again shortly.";
    }
    if (err.status === 503 || err.code === 3101) {
      return "Tracking is temporarily unavailable. Please try again later.";
    }
    if (err.status === 400 || err.code === 2000) {
      return "Please enter the 31-digit number printed below the barcode.";
    }
  }
  return "We couldn't reach the tracking service. Please try again in a moment.";
}
